import { Buses_Bool_Exp, Queue_Projects_Insert_Input } from "generated/graphql";
import { RegionType } from "types/regionType";
import {
    isKnownUserScopeStatus,
    USER_SCOPE_STATUS_TO_DISPLAY_STRING
} from "types/scope";
import { ScopeViewType } from "types/scopeViewType";

/**
 * Speculative ("new") projects: user-authored queue projects that aren't in the
 * base queue file. They are written to `queue_projects` alongside the ordinary
 * include/exclude toggle rows when a user scope is created, flagged with
 * `is_speculative`, and the pipeline models them as additional queue
 * generators. See the pipeline handoff contract for the invariants:
 *
 * - `project_number` is app-generated (`spec_1` … `spec_99`), max 7 characters
 *   (TARA's `NIRA-<project_number>` 12-char bus-name constraint), unique per
 *   scope and stable across re-runs. Real queue numbers never use the `spec_`
 *   namespace, so the prefix can't collide.
 * - Rows must be written in the same transaction that creates the scope, so
 *   they exist before the pipeline reads them at kickoff.
 * - One row per scope_view, with identical user fields across views.
 * - Speculative rows are always `included = true`.
 */

const SPECULATIVE_PROJECT_NUMBER_PREFIX = "spec_";

// `spec_99` is 7 characters — the longest project number that fits TARA's
// 12-character `NIRA-<project_number>` bus-name constraint.
export const MAX_SPECULATIVE_PROJECTS = 99;

// Nameplate bounds validated identically by the pipeline; keep in sync.
export const MIN_NAMEPLATE_MW = 20;
export const MAX_NAMEPLATE_MW = 3000;

/**
 * Lower bound the charging input advertises. The pipeline negates charging MW
 * into PMin, so a draw of zero is not a battery.
 */
export const MIN_CHARGING_MW = 1;

/** Default charge draw, matching the queue-update pipeline's `infer_charging_size`. */
export const CHARGING_RATIO = 0.8;

/** Where an unmatched fuel type lands. Must stay one of the options below. */
const FALLBACK_FUEL_TYPE = "other";

/**
 * WECC fuel types offered for speculative projects. Values are the pipeline's
 * own `FuelType` enum strings — "Solar + Storage" does not exist in WECC,
 * `hybrid` is a single value.
 */
export const SPECULATIVE_FUEL_TYPE_OPTIONS: ReadonlyArray<{
    readonly value: string;
    readonly label: string;
}> = [
    { value: "solar", label: "Solar" },
    { value: "wind", label: "Wind" },
    { value: "storage", label: "Storage" },
    { value: "hybrid", label: "Hybrid" },
    { value: "pump_storage", label: "Pumped Storage" },
    { value: "natural_gas", label: "Natural Gas" },
    { value: "geothermal", label: "Geothermal" },
    { value: FALLBACK_FUEL_TYPE, label: "Other" }
];

/**
 * Coerces a stored fuel type back onto the offered options.
 *
 * Copied projects carry whatever fuel type was written when their source study
 * was created, and the option list can change between then and now. An
 * unmatched value would leave the row's select showing its first option while
 * the draft held something else, so it lands on "Other" instead — visible, and
 * one click from correct.
 */
export const normalizeSpeculativeFuelType = (fuelType: string): string =>
    SPECULATIVE_FUEL_TYPE_OPTIONS.some((option) => option.value === fuelType)
        ? fuelType
        : FALLBACK_FUEL_TYPE;

/** The label the fuel-type select shows, for copy that names the fuel type. */
export const getSpeculativeFuelTypeLabel = (fuelType: string): string =>
    SPECULATIVE_FUEL_TYPE_OPTIONS.find((option) => option.value === fuelType)
        ?.label ?? fuelType;

/** Charging MW is only meaningful — and only accepted by the pipeline — for these. */
const CHARGING_FUEL_TYPES: ReadonlySet<string> = new Set([
    "storage",
    "hybrid",
    "pump_storage"
]);

export const acceptsChargingMw = (fuelType: string): boolean =>
    CHARGING_FUEL_TYPES.has(fuelType);

/** A bus the user can pick as the POI. Only buses this study can use are
 *  offered: in the base scope's models, so the pipeline can find
 *  `nira_bus_id`, and in a utility the study bus resolves to, so the study
 *  dispatches the project at all. See `buildPoiEligibilityFilters`. */
export type SpeculativePoiBus = {
    readonly busId: string;
    readonly niraBusId: string;
    readonly displayName: string;
    /**
     * The bus's own utility, formatted the way the step names the study bus's
     * (see `formatStudyBusUtilities`). Shown on the dropdown row so the rule
     * the list is filtered by is legible on each candidate, not only in the
     * sentence above the table. Undefined outside WECC, and for a bus the
     * pipeline never stamped.
     */
    readonly utilities: string | undefined;
};

export const formatSpeculativePoiBus = (bus: SpeculativePoiBus): string =>
    `${bus.displayName} (${bus.busId})`;

/**
 * A `buses` row as the two POI queries select it, declared structurally so the
 * conversion below can be exercised without building generated query types.
 */
export type SpeculativePoiBusRow = {
    readonly id?: string | null;
    readonly nira_bus_id?: string | null;
    readonly bus_display_name?: string | null;
    readonly utility_discharging?: string | null;
    readonly utility_storage?: string | null;
};

/**
 * Converts POI query rows into pickable buses, dropping any row missing part
 * of a POI's identity — Hasura types every column as nullable, and a bus the
 * app cannot name or hand to the pipeline is not offerable.
 *
 * Shared by the three places a POI arrives — typed into the step's typeahead,
 * copied from a previous study, re-resolved after a base-scope change — so all
 * three agree on what makes a row usable, the way
 * `buildPoiEligibilityFilters` makes them agree on what makes a bus eligible.
 */
export const toSpeculativePoiBuses = (
    rows: ReadonlyArray<SpeculativePoiBusRow>
): ReadonlyArray<SpeculativePoiBus> =>
    rows
        .map((row): SpeculativePoiBus | undefined =>
            row.id == null ||
            row.nira_bus_id == null ||
            row.bus_display_name == null
                ? undefined
                : {
                      busId: row.id,
                      niraBusId: row.nira_bus_id,
                      displayName: row.bus_display_name,
                      // Only for display. A missing utility does not
                      // disqualify a row: the server already applied the
                      // filter, and a bus that came back matched it.
                      utilities: formatStudyBusUtilities({
                          utilityDischarging:
                              row.utility_discharging ?? undefined,
                          utilityStorage: row.utility_storage ?? undefined
                      })
                  }
        )
        .filter((bus): bus is SpeculativePoiBus => bus !== undefined);

/** The same conversion, keyed by `nira_bus_id` for the two lookup call sites. */
export const toSpeculativePoiBusesByNiraBusId = (
    rows: ReadonlyArray<SpeculativePoiBusRow>
): ReadonlyMap<string, SpeculativePoiBus> =>
    new Map(toSpeculativePoiBuses(rows).map((bus) => [bus.niraBusId, bus]));

/**
 * The study bus fields the utility screen reads: a structural subset of `Bus`,
 * so callers can pass the bus straight through.
 */
export type StudyBusUtilities = {
    readonly utilityDischarging?: string;
    readonly utilityStorage?: string;
};

/**
 * The two directions a WECC user scope is studied in, and how each one screens
 * a candidate bus.
 *
 * Not keyed on the selected scope views, deliberately. Both directions run for
 * every WECC user scope: `wecc_post_processing_user_scope` takes
 * `discharging_flowgate_analysis_user_scope` and `charging_fcitc_user_scope`
 * as hard dependencies, and the job materializes everything upstream of the
 * upload. The user's fuel types decide which results they read, not which
 * studies run, and the storage view's results are the two studies combined. So
 * a POI is screened against both directions whatever the user selected.
 *
 * Each direction resolves the utility inside its own case (heavy summer for
 * discharging, light spring for storage), which is why there are two columns
 * and why they can disagree about a bus.
 *
 * Read and match live in one entry so the utility a message names is always
 * the one the filter used.
 */
const UTILITY_SCREENS: ReadonlyArray<{
    readonly read: (studyBus: StudyBusUtilities) => string | undefined;
    readonly match: (utility: string) => Buses_Bool_Exp;
}> = [
    {
        read: (studyBus) => studyBus.utilityDischarging,
        match: (utility) => ({ utility_discharging: { _eq: utility } })
    },
    {
        read: (studyBus) => studyBus.utilityStorage,
        match: (utility) => ({ utility_storage: { _eq: utility } })
    }
];

/**
 * The utilities a POI can belong to, for copy that names them, or undefined
 * when nothing restricts them — which is exactly when the filter below adds no
 * utility clause. Upper-cased because the product already shows these slugs
 * that way round, in the utility-prefixed queue project numbers (`ipc_718`).
 *
 * Normally one utility. Two, joined the way the filter reads them, only when
 * the two studies resolve the study bus differently, which the WECC cases do
 * for a handful of buses.
 */
export const formatStudyBusUtilities = (
    studyBus: StudyBusUtilities
): string | undefined => {
    const utilities = Array.from(
        new Set(
            UTILITY_SCREENS.map((screen) => screen.read(studyBus)).filter(
                (utility): utility is string => utility !== undefined
            )
        )
    );
    return utilities.length === 0
        ? undefined
        : utilities.map((utility) => utility.toUpperCase()).join(" or ");
};

/**
 * The `buses.where` clauses that restrict POI candidates to buses this study
 * can actually use, ANDed by the caller's query.
 *
 * Two halves, quantified differently because their failure modes differ:
 *
 * - a `bus_to_scopes` row for the base scope, one clause per selected scope
 *   view, so all are required. A bus missing from a view's models fails that
 *   view's run outright (`SpeculativePoiBusMissingFromModelError`). Per view
 *   rather than a single `scope_view: { _in: ... }`, which would ask for
 *   membership in any one of them.
 * - the study bus's utility, one OR clause over both study directions (see
 *   `UTILITY_SCREENS`). WECC dispatches a queue generator against a study bus
 *   only when the two resolve to the same utility
 *   (`get_nearby_queued_generator_bus_numbers` and its storage twin), so a POI
 *   matching neither direction is modelled at Pgen 0 and screened out of both:
 *   the study comes back as if the project had never been submitted. Matching
 *   one direction is enough, because that direction's results do move, and the
 *   two disagree about a bus often enough to matter (143 WECC buses in the
 *   2026 Q3 vintage, whose two cases give them different owners; requiring
 *   both left such a study bus 14 POIs instead of ~2,100).
 *
 * A direction whose utility the study bus lacks contributes nothing to the OR
 * (outside the WECC control areas, or a vintage published before the columns
 * existed), and a study bus with no utility at all leaves the POIs restricted
 * to the base scope, rather than to a clause nothing can satisfy.
 *
 * Shared so "is a valid POI" means the same thing wherever a POI is typed in,
 * copied, or re-resolved against a new base scope.
 *
 * An empty `scopeViews` yields an empty `_and`, which Hasura reads as "no
 * restriction" — callers must reject that case rather than pass it on.
 */
export const buildPoiEligibilityFilters = (
    scope: string,
    scopeViews: ReadonlyArray<ScopeViewType>,
    studyBus: StudyBusUtilities
): ReadonlyArray<Buses_Bool_Exp> => {
    const inBaseScope: Array<Buses_Bool_Exp> = scopeViews.map((scopeView) => ({
        bus_to_scopes: {
            _and: [
                { scope: { _eq: scope } },
                { scope_view: { _eq: scopeView } }
            ]
        }
    }));
    const utilityMatches = UTILITY_SCREENS.map((screen) => {
        const utility = screen.read(studyBus);
        return utility === undefined ? undefined : screen.match(utility);
    }).filter((match): match is Buses_Bool_Exp => match !== undefined);
    return utilityMatches.length === 0
        ? inBaseScope
        : [...inBaseScope, { _or: utilityMatches }];
};

/** A row of the "New projects" wizard step, possibly incomplete while editing. */
export type SpeculativeProjectDraft = {
    readonly id: string;
    readonly name: string;
    readonly poiBus: SpeculativePoiBus | undefined;
    /**
     * Set only on a copied row whose source POI this study cannot use: the bus
     * is missing from the base scope's models, or sits outside every utility
     * the study bus resolves to (see `buildPoiEligibilityFilters`). The POI
     * could not come across and the user has to pick a different one. Held
     * separately from `poiBus` so the row can say which bus was dropped.
     */
    readonly unavailablePoiBusId: string | undefined;
    /**
     * `nira_bus_id` of the POI this row could not resolve in the current base
     * scope. Kept alongside `unavailablePoiBusId` so a later base-scope change
     * can look the bus up again. The displayed bus id cannot do that lookup.
     */
    readonly unresolvedPoiNiraBusId: string | undefined;
    readonly fuelType: string;
    readonly nameplateMw: number | undefined;
    readonly chargingMw: number | undefined;
};

let nextDraftId = 0;
export const makeEmptySpeculativeProjectDraft = (): SpeculativeProjectDraft => {
    nextDraftId += 1;
    return {
        id: `speculative-draft-${nextDraftId}`,
        name: "",
        poiBus: undefined,
        unavailablePoiBusId: undefined,
        unresolvedPoiNiraBusId: undefined,
        fuelType: "solar",
        nameplateMw: undefined,
        chargingMw: undefined
    };
};

/**
 * The MW a Blueprint NumericInput is reporting, or undefined when its field is
 * empty.
 *
 * Blueprint derives the number with `Number(valueAsString)`, so a cleared field
 * arrives as 0, not NaN. Only the string distinguishes it from a typed zero,
 * and the difference matters: 0 is a validation error the row must report,
 * while a cleared field is a row still being filled in.
 */
export const readNumericInputMw = (
    valueAsNumber: number,
    valueAsString: string
): number | undefined => {
    if (valueAsString.trim().length === 0) {
        return undefined;
    }
    return Number.isFinite(valueAsNumber) ? valueAsNumber : undefined;
};

export const isNameplateMwValid = (nameplateMw: number | undefined): boolean =>
    nameplateMw !== undefined &&
    nameplateMw >= MIN_NAMEPLATE_MW &&
    nameplateMw <= MAX_NAMEPLATE_MW;

/**
 * Why the row's charging MW is rejected, or undefined when it is acceptable.
 *
 * A missing draw and an out-of-range one are different mistakes, so they read
 * differently: only the first is answered by typing anything at all. Blueprint's
 * NumericInput does not clamp a typed value, so the bounds are checked here.
 */
export const getChargingMwError = (
    draft: SpeculativeProjectDraft
): string | undefined => {
    if (!acceptsChargingMw(draft.fuelType)) {
        return undefined;
    }
    if (draft.chargingMw === undefined) {
        return `Required for ${getSpeculativeFuelTypeLabel(draft.fuelType)}`;
    }
    if (
        draft.chargingMw < MIN_CHARGING_MW ||
        draft.chargingMw > MAX_NAMEPLATE_MW
    ) {
        return `${MIN_CHARGING_MW}–${MAX_NAMEPLATE_MW} MW`;
    }
    return undefined;
};

const isChargingMwValid = (draft: SpeculativeProjectDraft): boolean =>
    getChargingMwError(draft) === undefined;

export const isSpeculativeProjectDraftComplete = (
    draft: SpeculativeProjectDraft
): boolean =>
    draft.name.trim().length > 0 &&
    draft.poiBus !== undefined &&
    isNameplateMwValid(draft.nameplateMw) &&
    isChargingMwValid(draft);

export const areSpeculativeProjectDraftsValid = (
    drafts: ReadonlyArray<SpeculativeProjectDraft>
): boolean =>
    drafts.length <= MAX_SPECULATIVE_PROJECTS &&
    drafts.every(isSpeculativeProjectDraftComplete);

/**
 * Builds the `queue_projects` insert inputs for the speculative projects of a
 * new user scope: one row per (project × scope_view) — matching how toggle
 * rows are written — with identical user fields across views, as the pipeline's
 * uniqueness validation requires. Project numbers are assigned `spec_1` …
 * `spec_N` in row order; because the rows are written exactly once, at scope
 * creation, they are stable across pipeline re-runs of the scope.
 */
export const buildSpeculativeQueueProjectInsertInputs = (
    drafts: ReadonlyArray<SpeculativeProjectDraft>,
    scopeViews: ReadonlyArray<string>,
    region: RegionType,
    scope: string
): ReadonlyArray<Queue_Projects_Insert_Input> => {
    if (!areSpeculativeProjectDraftsValid(drafts)) {
        throw new Error(
            "Cannot build speculative queue project inserts from invalid drafts"
        );
    }
    return scopeViews.flatMap((scopeView) =>
        drafts.map((draft, index): Queue_Projects_Insert_Input => {
            // Validated above: poiBus and nameplateMw are set on every draft.
            if (draft.poiBus === undefined || draft.nameplateMw === undefined) {
                throw new Error("Incomplete speculative project draft");
            }
            return {
                project_number: `${SPECULATIVE_PROJECT_NUMBER_PREFIX}${
                    index + 1
                }`,
                is_speculative: true,
                poi_description: draft.name.trim(),
                bus_id: draft.poiBus.busId,
                nira_bus_id: draft.poiBus.niraBusId,
                fuel_type: draft.fuelType,
                eris_capacity_mw: draft.nameplateMw,
                nris_capacity_mw: null,
                charging_capacity_mw: acceptsChargingMw(draft.fuelType)
                    ? draft.chargingMw ?? null
                    : null,
                // The pipeline always models speculative rows as included; the
                // explicit true keeps the queue-assumptions display honest.
                included: true,
                // Every other row in this column carries a utility slug (`pace`,
                // `pacw`, `bpa`). The bus's `owner_name` is not one — it is a
                // Velocity Suite company name that merges pace and pacw into a
                // single "PacifiCorp" — so writing it here invented a
                // sub-region that does not exist. The real value is
                // `buses.utility`, which is NULL until nira-data-pipeline#8026
                // lands and WECC republishes; the pipeline never reads
                // sub_region off a speculative row, so leave it empty until
                // then rather than carry something wrong.
                sub_region: "",
                cluster: null,
                facility_id: null,
                status: null,
                scope,
                scope_view: scopeView,
                region
            };
        })
    );
};

/**
 * A speculative project on one of the team's earlier local studies, offered
 * for copying into the study being created.
 */
export type PriorSpeculativeProject = {
    /** `spec_N` as assigned in the source study; unique within that study. */
    readonly projectNumber: string;
    readonly name: string;
    readonly niraBusId: string;
    readonly busId: string;
    readonly fuelType: string;
    readonly nameplateMw: number;
    readonly chargingMw: number | undefined;
};

/** An earlier local study that has at least one speculative project. */
export type PriorSpeculativeStudy = {
    readonly scope: string;
    readonly displayName: string;
    /** Display name of the study's own bus, not of any project's POI. */
    readonly busLabel: string;
    readonly createdAt: Date | undefined;
    readonly statusLabel: string | undefined;
    readonly projects: ReadonlyArray<PriorSpeculativeProject>;
};

/**
 * A `getSpeculativeProjectsForCopy` row, declared structurally so the
 * conversion below can be exercised without building generated query types.
 */
export type HasuraSpeculativeProjectRow = {
    readonly scope: string;
    readonly project_number: string;
    readonly poi_description: string;
    readonly bus_id?: string | null;
    readonly nira_bus_id?: string | null;
    readonly fuel_type: string;
    readonly eris_capacity_mw: number;
    readonly charging_capacity_mw?: number | null;
    readonly scope_info?: {
        readonly display_name: string;
        readonly user_scope_config?: {
            readonly status: string;
            readonly created_at: string;
        } | null;
        readonly buses_to_scopes: ReadonlyArray<{
            readonly bus?: {
                readonly bus_display_name?: string | null;
            } | null;
        }>;
    } | null;
};

/** Sort key for `spec_N`, so spec_2 precedes spec_10 as the user entered them. */
const speculativeProjectOrder = (projectNumber: string): number => {
    const suffix = Number(
        projectNumber.slice(SPECULATIVE_PROJECT_NUMBER_PREFIX.length)
    );
    return Number.isFinite(suffix) ? suffix : Number.MAX_SAFE_INTEGER;
};

/**
 * Groups the flat query rows into one entry per source study, newest first.
 *
 * A row whose POI bus is missing is dropped: `nira_bus_id` is what the copy
 * resolves against the new base scope, and without it the project cannot be
 * carried over at all. A study left with no projects drops out with it.
 */
export const convertToPriorSpeculativeStudies = (
    rows: ReadonlyArray<HasuraSpeculativeProjectRow>
): ReadonlyArray<PriorSpeculativeStudy> => {
    const byScope = new Map<string, PriorSpeculativeStudy>();

    rows.forEach((row) => {
        const scopeInfo = row.scope_info;
        if (
            scopeInfo === undefined ||
            scopeInfo === null ||
            row.nira_bus_id === undefined ||
            row.nira_bus_id === null ||
            row.bus_id === undefined ||
            row.bus_id === null
        ) {
            return;
        }
        const config = scopeInfo.user_scope_config;
        const status = config?.status;
        const existing = byScope.get(row.scope);
        const study: PriorSpeculativeStudy = existing ?? {
            scope: row.scope,
            displayName: scopeInfo.display_name,
            busLabel:
                scopeInfo.buses_to_scopes[0]?.bus?.bus_display_name ?? "—",
            createdAt:
                config === undefined || config === null
                    ? undefined
                    : new Date(config.created_at),
            statusLabel:
                status !== undefined && isKnownUserScopeStatus(status)
                    ? USER_SCOPE_STATUS_TO_DISPLAY_STRING[status]
                    : status,
            projects: []
        };
        byScope.set(row.scope, {
            ...study,
            projects: [
                ...study.projects,
                {
                    projectNumber: row.project_number,
                    name: row.poi_description,
                    niraBusId: row.nira_bus_id,
                    busId: row.bus_id,
                    fuelType: row.fuel_type,
                    nameplateMw: row.eris_capacity_mw,
                    chargingMw: row.charging_capacity_mw ?? undefined
                }
            ]
        });
    });

    return Array.from(byScope.values())
        .map((study) => ({
            ...study,
            projects: [...study.projects].sort(
                (a, b) =>
                    speculativeProjectOrder(a.projectNumber) -
                    speculativeProjectOrder(b.projectNumber)
            )
        }))
        .sort(
            (a, b) =>
                (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0)
        );
};

/** The `display name · bus · N projects · date · status` line under a study. */
export const formatPriorSpeculativeStudySubtitle = (
    study: PriorSpeculativeStudy
): string =>
    [
        study.busLabel,
        `${study.projects.length} ${
            study.projects.length === 1 ? "project" : "projects"
        }`,
        study.createdAt?.toLocaleDateString(),
        study.statusLabel
    ]
        .filter((part): part is string => part !== undefined)
        .join(" · ");

/**
 * Whether copying `selectedCount` projects on top of `existingCount` already
 * added stays inside the `spec_N` namespace. The cap is on the whole step, so
 * the copy is blocked rather than truncated — silently dropping the tail would
 * be worse than making the user deselect.
 */
export const canCopySpeculativeProjects = (
    existingCount: number,
    selectedCount: number
): boolean =>
    selectedCount > 0 &&
    existingCount + selectedCount <= MAX_SPECULATIVE_PROJECTS;

/**
 * Turns projects picked out of a previous study into rows of this study's
 * "New projects" step.
 *
 * `poiBusesByNiraBusId` holds the source POIs that resolved against the new
 * base scope. A project whose POI didn't resolve is still copied — with its
 * name, fuel type and sizes intact — but with the POI blank and the dropped
 * bus recorded, so the row shows what is missing instead of the project
 * disappearing without explanation.
 */
export const makeSpeculativeProjectDraftsFromPriorProjects = (
    projects: ReadonlyArray<PriorSpeculativeProject>,
    poiBusesByNiraBusId: ReadonlyMap<string, SpeculativePoiBus>
): ReadonlyArray<SpeculativeProjectDraft> =>
    projects.map((project) => {
        const poiBus = poiBusesByNiraBusId.get(project.niraBusId);
        const fuelType = normalizeSpeculativeFuelType(project.fuelType);
        return {
            ...makeEmptySpeculativeProjectDraft(),
            name: project.name,
            poiBus,
            unavailablePoiBusId:
                poiBus === undefined ? project.busId : undefined,
            unresolvedPoiNiraBusId:
                poiBus === undefined ? project.niraBusId : undefined,
            fuelType,
            nameplateMw: project.nameplateMw,
            chargingMw: acceptsChargingMw(fuelType)
                ? project.chargingMw
                : undefined
        };
    });

/**
 * The result of blanking the New projects rows' POIs on a base-scope change:
 * the rows, plus the `nira_bus_id` each one's POI was.
 *
 * The ids are what `restoreSpeculativeProjectDraftPois` resolves against the
 * new scope, and they have to be collected before the blanking — a blanked row
 * keeps only the bus id it shows the user, which the by-`nira_bus_id` lookup
 * cannot use.
 */
export type ClearedSpeculativeProjectPois = {
    readonly drafts: ReadonlyArray<SpeculativeProjectDraft>;
    /**
     * `nira_bus_id` to look up for each row, keyed by row id: the POI the row
     * held, or the one a copied row is still waiting on.
     */
    readonly niraBusIdsByDraftId: ReadonlyMap<string, string>;
};

/**
 * Blanks every row's POI, recording the bus it held so the row can say which
 * one it lost.
 *
 * Called when the base scope changes: a POI is only valid in the scope it was
 * picked from — base scopes differ in bus coverage — so it cannot be carried
 * over unchecked, or Create would insert a `nira_bus_id` the pipeline can't
 * find in the study model. Everything else the user typed survives, and
 * `restoreSpeculativeProjectDraftPois` puts back the POIs the new scope has.
 */
export const clearSpeculativeProjectDraftPois = (
    drafts: ReadonlyArray<SpeculativeProjectDraft>
): ClearedSpeculativeProjectPois => {
    const niraBusIdsByDraftId = new Map<string, string>();
    const cleared = drafts.map((draft): SpeculativeProjectDraft => {
        if (draft.poiBus === undefined) {
            // A copied row whose POI never resolved here still knows the bus it
            // wants, and the new scope may have it.
            if (draft.unresolvedPoiNiraBusId !== undefined) {
                niraBusIdsByDraftId.set(draft.id, draft.unresolvedPoiNiraBusId);
            }
            return draft;
        }
        niraBusIdsByDraftId.set(draft.id, draft.poiBus.niraBusId);
        return {
            ...draft,
            poiBus: undefined,
            unavailablePoiBusId: draft.poiBus.busId
        };
    });
    return { drafts: cleared, niraBusIdsByDraftId };
};

/**
 * Puts back the POIs that resolved against the new base scope.
 *
 * `poiBusesByNiraBusId` is what the new scope has. A row whose POI didn't
 * resolve keeps the blank POI and the dropped bus, so it shows what is missing
 * instead of the project quietly losing its POI — the same treatment the
 * copy-from-prior-study path gives a POI it can't carry over. A row the user
 * has already re-pointed is left alone: their pick came from this scope's own
 * typeahead, so it is at least as good as the one being restored.
 */
export const restoreSpeculativeProjectDraftPois = (
    drafts: ReadonlyArray<SpeculativeProjectDraft>,
    niraBusIdsByDraftId: ReadonlyMap<string, string>,
    poiBusesByNiraBusId: ReadonlyMap<string, SpeculativePoiBus>
): ReadonlyArray<SpeculativeProjectDraft> =>
    drafts.map((draft): SpeculativeProjectDraft => {
        const niraBusId = niraBusIdsByDraftId.get(draft.id);
        const poiBus =
            niraBusId === undefined
                ? undefined
                : poiBusesByNiraBusId.get(niraBusId);
        return poiBus === undefined || draft.poiBus !== undefined
            ? draft
            : {
                  ...draft,
                  poiBus,
                  unavailablePoiBusId: undefined,
                  unresolvedPoiNiraBusId: undefined
              };
    });
